import { FC, useContext, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { BackwardIcon, Button, H3, H2 } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useForm, useWatch } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router-dom';

import { TariffCard } from 'components';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { TariffContext } from 'pages/Tariffs/TariffContext/TariffContext';
import { CREATE_TARIFF, DELETE_TARIFF, UPDATE_TARIFF } from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';
import { ESettingsAliases, SettingsContext } from 'settings';
import {
  EDuration,
  ETariffType,
  INormalizedTariff,
  IServerTariff,
} from 'types/tariff';
import { normalizeTariff, prepareBeforeMutation } from 'utils/tariff';

import { BOOKABLE_TARIFFS, DEFAULT_VALUES } from './constants';
import { PriceList } from './PriceList';
import s from './Tariff.module.scss';
import {
  TariffAdditionalSection,
  TariffEanSection,
  TariffLifetimeSection,
  TariffScheduleSection,
  TariffTitleSections,
  TariffTypeSection,
} from './TariffFormSections';

export const Tariff: FC = () => {
  const { t } = useContext(LocalizationContext);
  const size = useWindowResize();

  const {
    state: {
      settings: { fastLoginAutostartSessionTariffId },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);

  const {
    control,
    reset,
    handleSubmit,
    getValues,
    trigger,
    setValue,
    watch,
    setError,

    formState: { isDirty },
  } = useForm<INormalizedTariff>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const navigate = useNavigate();

  const values = watch();
  const {
    duration,
    duration_type: durationType,
    lifetime,
    lifetime_type: lifetimeType,
    title: tariffTitle,
    type: tariffType,
    show_in_shell: showInShell,
  } = values;

  const [isSending, setIsSending] = useState(false);

  const { tariff } = useContext(TariffContext);
  const priceList = useWatch({ control, name: 'price_list' });

  useEffect(() => {
    if (
      tariff?.disable_sale_receipt_printing &&
      priceList?.every((item) => item.cost !== 0 && item.user_cost !== 0)
    ) {
      setValue('disable_sale_receipt_printing', false);
    }
  }, [
    priceList ? JSON.stringify(priceList) : '[]',
    tariff?.disable_sale_receipt_printing,
  ]);

  useEffect(() => {
    if (duration) {
      trigger('duration');
    }
  }, [durationType, duration, trigger]);
  useEffect(() => {
    if (tariffType === ETariffType.PAUSABLE) {
      trigger('lifetime');
    }
  }, [lifetime, lifetimeType, durationType, duration, trigger]);

  useEffect(() => {
    if (tariff) {
      reset(normalizeTariff(tariff, t));
    }
  }, [tariff, reset]);

  const [createTariff] = useMutation<{ input: IServerTariff }>(CREATE_TARIFF, {
    onCompleted: () => {
      notification.info({
        t,
        leftDescription: '',
        title: t('tariff.created'),
        message: t('tariff.created.description', tariffTitle),
        type: 'success',
        dismiss: 3,
      });
      setIsSending(false);
      navigate('/tariffs', { replace: true });
    },
    onError: (error) => {
      let message = t('tariff.created.error.message');

      if (error.message === 'schedule has intersections') {
        message = t('tariff.error.intersection');
      }

      if (error.message === 'ean is already taken') {
        message = t('errors.ean.inUse');
        setError('ean', { message });
      }

      notification.info({
        t,
        leftDescription: '',
        title: t('tariff.created.error'),
        message,
        type: 'error',
        dismiss: 3,
      });
      setIsSending(false);
    },
  });

  const [updateTariff] = useMutation<{ updateTariff: IServerTariff }>(
    UPDATE_TARIFF,
    {
      onCompleted: (data) => {
        if (!data.updateTariff) return;

        notification.info({
          t,
          leftDescription: '',
          title: t('tariff.updated'),
          message: t('tariff.updated.description', tariffTitle),
          type: 'success',
          dismiss: 3,
        });

        if (fastLoginAutostartSessionTariffId === tariff?.id) {
          setSetting({
            alias:
              ESettingsAliases.SHELL_FAST_LOGIN_AUTOSTART_SESSION_TARIFF_ID,
            value: String(data.updateTariff.id),
          });
        }

        navigate('/tariffs', { replace: true });
      },
      onError: (error) => {
        let message = t('tariff.updated.error.message');

        if (error.message === 'schedule has intersections') {
          message = t('tariff.error.intersection');
        }

        if (error.message === 'tariff is used in combo') {
          message = t('tariff.error.combo');
        }

        if (error.message === 'ean is already taken') {
          message = t('errors.ean.inUse');
          setError('ean', { message });
        }

        notification.info({
          t,
          leftDescription: '',
          title: t('tariff.updated.error'),
          message,
          type: 'error',
          dismiss: 3,
        });
        setIsSending(false);
      },
    }
  );

  const [deleteTariff] = useMutation<{ id: number }>(DELETE_TARIFF, {
    onCompleted: () => {
      notification.info({
        t,
        leftDescription: '',
        title: t('tariff.deleted'),
        message: t('tariff.deleted.description', tariffTitle),
        type: 'success',
        dismiss: 3,
      });
      setIsSending(false);
      navigate('/tariffs', { replace: true });
    },
    onError: (error) => {
      let message = t('tariff.deleted.error.message');

      if (error.message === 'tariff is used in combo') {
        message = t('tariff.error.combo');
      }

      notification.info({
        t,
        leftDescription: '',
        title: t('tariff.deleted.error'),
        message: message,
        type: 'error',
        dismiss: 3,
      });
      setIsSending(false);
    },
    update: (cache, result) => {
      if (!result.data?.id) return;

      if (fastLoginAutostartSessionTariffId === tariff?.id) {
        setSetting({
          alias: ESettingsAliases.SHELL_FAST_LOGIN_AUTOSTART_SESSION_TARIFF_ID,
          value: '0',
        });
      }
    },
  });

  useEffect(() => {
    if (tariffType === 'PER_MINUTE') {
      setValue('duration', 1);
      setValue('duration_type', EDuration.minutes);
    }

    if (!BOOKABLE_TARIFFS.includes(tariffType)) {
      setValue('online_booking_enabled', false);
    }
  }, [tariffType]);

  return (
    <div className={s.root}>
      <NavLink to={'/tariffs'} className={s.link}>
        <BackwardIcon />
        {t('back')}
      </NavLink>
      <div className={s.container}>
        <form
          className={s.form}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          onSubmit={handleSubmit((values) => {
            setIsSending(true);
            const result = prepareBeforeMutation(values);

            if (tariff?.id) {
              updateTariff({ variables: { id: tariff.id, input: result } });
              return;
            }
            createTariff({ variables: { input: result } });
          })}
        >
          <H2>{t(tariff ? 'tariff.editing' : 'tariff.add')}</H2>

          <TariffTitleSections control={control} />

          <TariffTypeSection
            control={control}
            tariffType={tariffType}
            getValues={getValues}
          />

          {tariffType === 'PAUSABLE' && (
            <TariffLifetimeSection control={control} getValues={getValues} />
          )}

          <div className={s['form-block']}>
            <H3 className={s.header}>{t('tariff.priceList')}</H3>
            <PriceList control={control} />
          </div>

          <TariffScheduleSection
            control={control}
            tariffType={tariffType}
            setValue={setValue}
            trigger={trigger}
            isDirty={isDirty}
          />

          <TariffEanSection control={control} />

          <TariffAdditionalSection
            control={control}
            tariffType={tariffType}
            setValue={setValue}
            durationType={durationType}
            watch={watch}
            duration={duration}
            showInShell={showInShell}
          />

          {size.width <= 960 && (
            <div className={cn(s.preview, s.preview_mobile)}>
              <H3>{t('tariff.preview.title')}</H3>
              <TariffCard preview={true} tariff={values} />
            </div>
          )}
          <div className={s['buttons-container']}>
            {tariff && (
              <Button
                disabled={isSending}
                variant="outline"
                className={s['remove-button']}
                color="red"
                onClick={() => {
                  setIsSending(true);
                  openConfirmDialog(t, 'remove', {
                    title: t('tariff.removing'),
                    message: t('tariff.removing.message', tariff.title),
                  })
                    .then(({ status }) => {
                      if (status === 'ACCEPTED') {
                        return deleteTariff({ variables: { id: tariff.id } });
                      }
                    })
                    .finally(() => setIsSending(false));
                }}
                iconName="BucketIcon"
                onlyIcon
              />
            )}
            <Button
              className={!tariff ? s.button_submit : ''}
              type="submit"
              disabled={!isDirty || isSending}
              fullWidth
            >
              {t(tariff ? 'save' : 'add')}
            </Button>
          </div>
        </form>
        {size.width > 960 && (
          <div className={s.preview}>
            <H3>{t('tariff.preview.title')}</H3>
            <TariffCard preview={true} tariff={values} />
          </div>
        )}
      </div>
    </div>
  );
};
